import moment from 'moment';
import mapValues from 'lodash/mapValues';
import AgeRangeReaches from 'common/domain/AgeRangeReaches';
import GenderReach from 'common/domain/GenderReach';
import DateRange from 'common/domain/DateRange';
import Geolocation from 'common/domain/Geolocation';
import GeolocationReach from './GeolocationReach';
import AntennaReach from '../../../domain/AntennaReach';
import AgeRangeReachesAPIHelper from '../../../../common/helpers/AgeRangeReachesAPIHelper';

export type Period = 'monthly' | 'weekly';
export type hourlyMetricsType = 'reach' | 'listeningTime';
export type GeoMapTypes = 'byAntennas' | 'byRegion';
export type MetricType = hourlyMetricsType;

export type ReachByGender = {
  male: number;
  female: number;
}

type ReachByRegion = {
  geolocation: Geolocation,
  reach: number
}

export type ComparedMetric = {
  current: number;
  compared: number;
}

export type ComparedMetricByTime = {
  [moment]: ComparedMetric
};

const mapToHours = (seconds: number) => {
  return seconds / 3600;
};

const mapValuesToHours = (aMap: { [any]: number }): { [any]: moment.duration } => {
  return mapValues(aMap, mapToHours);
};

export function calculateMetricVariation(data: ComparedMetric) {
  let metrics = data;
  if (data.current) {
    metrics = {};
    metrics[0] = data;
  }
  const keys = Object.keys(metrics);

  const comparedSum = keys.reduce((acc, key) => {
    return acc + metrics[key].compared;
  }, 0);
  const comparedAverage = comparedSum / keys.length;

  const currentSum = keys.reduce((acc, key) => {
    return acc + metrics[key].current;
  }, 0);
  const currentAverage = currentSum / keys.length;

  const variation = currentAverage - comparedAverage;
  return variation / comparedAverage;
}

export type DashboardMetrics = {
  averageReach: number,
  averageListeningTime: number,
  averageListeningTimePerPerson: number,

  saturdayComparedReach: ComparedMetric,
  sundayComparedReach: ComparedMetric,

  saturdayComparedListeningTime: ComparedMetricByTime,
  sundayComparedListeningTime: ComparedMetricByTime,
  comparedReachByTimeInterval: ComparedMetricByTime,
  comparedListeningTimeByTimeInterval: ComparedMetricByTime,
  reachByRegion?: ReachByRegion,
  reachByAgeRange: AgeRangeReaches,
  reachByGender: ReachByGender,
  antennaReach?: AntennaReach,

  isInterpolated: boolean,
  isAFallback: boolean,
};

export type WeeklyDashboardData = DashboardMetrics & {
  week: DateRange;
};

export type MonthlyDashboardData = DashboardMetrics & {
  cumulativeReach: number;
  cumulativeListeningTime: number;
  cumulativeListeningTimePerPerson: number;
  month: DateRange;
}

export function parseWeeklyDashboardData(data: Object): { [?string]: WeeklyDashboardData } {
  const weekly = {};
  weekly[undefined] = parseWeeklyDashboardDataMetrics(data);

  if (data.centralMarkets) {
    Object.keys(data.centralMarkets).forEach((reference) => {
      weekly[reference] = parseWeeklyDashboardDataMetrics(data.centralMarkets[reference]);
    });
  }
  return weekly;
}

function parseWeeklyDashboardDataMetrics(data: Object): WeeklyDashboardData {
  return {
    ...parseMetrics(data),
    week: DateRange.fromISO(data.week),
  };
}

export function parseMonthlyDashboardData(data: Object): { [?string]: MonthlyDashboardData } {
  const monthly = {};
  monthly[undefined] = parseMonthlyDashboardDataMetrics(data);

  if (data.centralMarkets) {
    Object.keys(data.centralMarkets).forEach((reference) => {
      monthly[reference] = parseMonthlyDashboardDataMetrics(data.centralMarkets[reference]);
    });
  }
  return monthly;
}

function parseMonthlyDashboardDataMetrics(data: Object): MonthlyDashboardData {
  return {
    ...parseMetrics(data),
    cumulativeReach: data.cumulativeReach,
    cumulativeListeningTime: mapToHours(data.cumulativeListeningTime),
    cumulativeListeningTimePerPerson: data.cumulativeListeningTimePerPerson ? mapToHours(data.cumulativeListeningTimePerPerson) : undefined,
    month: DateRange.fromISO(data.month),
  };
}

function parseMetrics(data: Object): DashboardMetrics {
  return {
    ...data,
    reachByAgeRange: AgeRangeReaches.parse(AgeRangeReachesAPIHelper.flatToJson(data.reachByAgeRange, data.reachByAgeRangeMetadata)),
    reachByGender: new GenderReach(data.reachByGender.female, data.reachByGender.male),
    averageListeningTime: mapToHours(data.averageListeningTime),
    averageListeningTimePerPerson: data.averageListeningTimePerPerson ? mapToHours(data.averageListeningTimePerPerson) : undefined,
    saturdayComparedListeningTime: mapValuesToHours(data.saturdayComparedListeningTime),
    sundayComparedListeningTime: mapValuesToHours(data.sundayComparedListeningTime),
    comparedListeningTimeByTimeInterval: mapValues(data.comparedListeningTimeByTimeInterval, mapValuesToHours),
    antennaReach: data.antennaReach ? AntennaReach.parse(data.antennaReach) : undefined,
    isInterpolated: data.isInterpolated || false,
    isAFallback: data.isAFallback,
    reachByRegion: data.reachByRegion ? data.reachByRegion.map(geo => GeolocationReach.parse(geo)) : undefined,
  };
}



// WEBPACK FOOTER //
// ./src/statsradio/modules/dashboard/domain/DashboardData.js